/**
 * AutoReply.jsx
 * Rules that answer comments privately, without you at the keyboard.
 *
 * This is the only screen in the app that can put words in front of a stranger
 * with nobody reading them first, so it is arranged around doubt rather than
 * around the rules. The master switch is at the top and says in a sentence what
 * it is currently doing. Underneath it is a preview of what would go out *right
 * now* — including the comments that would be passed over and why — because the
 * useful question before switching this on is not "what matches" but "what did
 * I expect to match and did not".
 *
 * A new rule is always created switched off. Turning it on is a second,
 * separate press, after you have read it back.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { useBrands } from '../hooks/useBrands.jsx';
import { Chat, Check, Warning, Plus, Refresh, Send } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const BLANK = { phrase: '', reply: '{name}, here you go: {link}', brand_id: '' };

export default function AutoReply() {
  const { brands } = useBrands();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [next, shown] = await Promise.all([api.autoreply.get(), api.autoreply.preview()]);
      setData(next);
      setPreview(shown);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn, message) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const result = await fn();
      setNote(typeof message === 'function' ? message(result) : message);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function switchOn() {
    const live = (data?.rules ?? []).filter((r) => r.enabled);
    if (live.length === 0) {
      setError(new Error('No rule is switched on yet, so this would send nothing. Add one first.'));
      return;
    }

    const ok = window.confirm(
      `Switch automatic replies on?\n\n` +
        `${live.length} rule${live.length === 1 ? '' : 's'} will start answering comments ` +
        `privately, on their own, without you seeing them first:\n\n` +
        live.map((r) => `  "${r.phrase}" → ${r.reply}`).join('\n') +
        `\n\nAt most ${data.limits.maxPerRun} go out per minute. You can switch this off at any time.`,
    );
    if (!ok) return;

    act(() => api.autoreply.setEnabled(true), 'Automatic replies are on.');
  }

  if (error && !data) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  const rules = data?.rules ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Auto-reply</div>
          <h1>Answer the same question once</h1>
          <p>
            The commonest comment on a post is somebody asking for the post. Write the reply once,
            and it goes to them privately — with the post’s link — whether or not you are awake.
          </p>
        </div>
        <div className="page-head__actions">
          <button className="btn" onClick={load} disabled={busy}>
            <Refresh /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {/* ---- the switch ---- */}
      <section className="card">
        <div className="row">
          <span className={`tile tile--sm ${data?.enabled ? 'tile--ok' : ''}`}>
            <Chat size={17} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="row__label">{data?.enabled ? 'On' : 'Off'}</div>
            <div className="row__sub">{data?.note}</div>
          </div>
          <div className="row__end">
            {data?.enabled ? (
              <>
                <button
                  className="btn btn--sm"
                  onClick={() => act(api.autoreply.runNow, (r) => `Sent ${r.sent}.`)}
                  disabled={busy}
                >
                  <Send size={15} /> Send now
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={() => act(() => api.autoreply.setEnabled(false), 'Switched off.')}
                  disabled={busy}
                >
                  Switch off
                </button>
              </>
            ) : (
              <button className="btn btn--sm btn--primary" onClick={switchOn} disabled={busy}>
                Switch on
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ---- what would happen ---- */}
      <section className="card">
        <h2>What would go out right now</h2>
        <p className="muted">
          Run against the comments waiting at this moment. Nothing here has been sent.
        </p>

        {!preview?.would_send?.length && !preview?.would_skip?.length && (
          <div className="row">
            <span className="tile tile--sm">
              <Check size={17} />
            </span>
            <div>
              <div className="row__label">No comments waiting</div>
              <div className="row__sub">
                Comments arrive with the sync, roughly every five minutes.
              </div>
            </div>
          </div>
        )}

        {preview?.would_send?.length > 0 && (
          <div className="rows">
            {preview.would_send.map((p) => (
              <div className="row row--wrap" key={p.comment_id}>
                <span className="tile tile--sm tile--ok">
                  <Send size={17} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row__label">
                    {p.author} on {p.brand} — “{p.comment}”
                  </div>
                  <div className="row__sub">
                    Matched “{p.phrase}” → <strong>{p.reply}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {preview?.would_skip?.length > 0 && (
          <>
            <h3 className="muted">Passed over</h3>
            <div className="rows">
              {preview.would_skip.map((p) => (
                <div className="row row--wrap" key={p.comment_id}>
                  <span className="tile tile--sm">
                    <Warning size={17} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="row__label">
                      {p.author} — “{p.comment}”
                    </div>
                    <div className="row__sub">{p.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ---- the rules ---- */}
      <section className="card">
        <h2>Rules</h2>
        <p className="muted">
          Tried in order, and the first match wins — so put a narrow rule above a broad one. Matching
          ignores capitals, punctuation and emoji.
        </p>

        <div className="rows">
          {rules.map((r) => (
            <div className="row row--wrap" key={r.id}>
              <span className={`tile tile--sm ${r.enabled ? 'tile--ok' : ''}`}>
                <Chat size={17} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="row__label">“{r.phrase}”</div>
                <div className="row__sub">
                  {r.reply}
                  {' · '}
                  {r.brand_name ?? 'All brands'}
                  {r.times_used > 0 && ` · used ${r.times_used} times`}
                  {r.last_used_at && `, last ${formatLondon(r.last_used_at)}`}
                </div>
              </div>
              <div className="row__end">
                <button
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () => api.autoreply.update(r.id, { enabled: !r.enabled }),
                      r.enabled ? 'Rule switched off.' : 'Rule switched on.',
                    )
                  }
                >
                  {r.enabled ? 'Switch off' : 'Switch on'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Delete the rule for “${r.phrase}”?`)) return;
                    act(() => api.autoreply.remove(r.id), 'Rule deleted.');
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          {rules.length === 0 && <p className="muted">No rules yet.</p>}
        </div>

        <h3>Add a rule</h3>
        <div className="form-grid">
          <label>
            <span>When a comment contains</span>
            <input
              type="text"
              value={draft.phrase}
              placeholder="send me this post"
              onChange={(e) => setDraft({ ...draft, phrase: e.target.value })}
            />
          </label>
          <label>
            <span>Reply privately with</span>
            <input
              type="text"
              value={draft.reply}
              onChange={(e) => setDraft({ ...draft, reply: e.target.value })}
            />
          </label>
          <label>
            <span>Brand</span>
            <select
              value={draft.brand_id}
              onChange={(e) => setDraft({ ...draft, brand_id: e.target.value })}
            >
              <option value="">All brands</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="muted">
          {Object.entries(data?.placeholders ?? {}).map(([token, means]) => (
            <span key={token}>
              <code>{token}</code> — {means}.{' '}
            </span>
          ))}
          A rule using <code>{'{link}'}</code> waits rather than guessing if Meta has not given us
          the post’s address yet.
        </p>

        <button
          className="btn btn--primary"
          disabled={busy || !draft.phrase.trim() || !draft.reply.trim()}
          onClick={() =>
            act(
              () =>
                api.autoreply.add({
                  phrase: draft.phrase.trim(),
                  reply: draft.reply.trim(),
                  brand_id: draft.brand_id || null,
                }),
              'Rule added, switched off. Read it back, then switch it on.',
            ).then(() => setDraft(BLANK))
          }
        >
          <Plus /> Add rule
        </button>
      </section>

      {/* ---- what it has done ---- */}
      <section className="card">
        <h2>Already sent</h2>
        <p className="muted">
          Every automatic reply, as it went out — kept even if the rule is edited afterwards.
        </p>

        {(data?.recent ?? []).length === 0 ? (
          <p className="muted">Nothing has been sent automatically yet.</p>
        ) : (
          <div className="rows">
            {data.recent.map((l) => (
              <div className="row row--wrap" key={l.id}>
                <span className={`tile tile--sm ${l.ok ? 'tile--ok' : 'tile--danger'}`}>
                  {l.ok ? <Check size={17} /> : <Warning size={17} />}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row__label">
                    {l.author_name} — “{l.matched}”
                  </div>
                  <div className="row__sub">
                    {l.ok ? l.sent : l.error} · {l.brand_name} · {formatLondon(l.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
